import { createContext, useContext, useState, useCallback, useEffect } from "react";

const ServiceContext = createContext(null);

const SELECTED_VEHICLE_KEY = "selectedCarId";

const STORED_VEHICLE_KEY = "selectedVehicle";

const getInitialServiceState = () => {
  const savedVehicleId = localStorage.getItem(SELECTED_VEHICLE_KEY);
  let savedVehicle = null;
  try {
    const raw = localStorage.getItem(STORED_VEHICLE_KEY);
    savedVehicle = raw ? JSON.parse(raw) : null;
  } catch {
    savedVehicle = null;
  }
  // Guard: stored vehicle must match stored id; otherwise stale (cross-customer).
  if (savedVehicle && String(savedVehicle.id) !== String(savedVehicleId)) {
    savedVehicle = null;
  }
  return {
    selectedVehicleId: savedVehicleId || null,
    selectedVehicle: savedVehicle,
    // Step 1 (service worker): customer picked/created by mobile search.
    // { id, firstName, lastName, phone, isNew } — id is null for new customers.
    selectedCustomer: null,
    currentMileage: 0,
    lastServiceMileage: null,
    nextSuggestedMileage: null,
    nextServiceMileage: null,
    selectedCategories: {},
    categoryDetails: {},
    pendingServices: [],
    serviceDate: new Date().toISOString().split('T')[0],
    cost: null,
    description: '',
    details: {},
  };
};

const initialServiceState = getInitialServiceState();

export function ServiceProvider({ children }) {
  const [serviceState, setServiceState] = useState(initialServiceState);

  useEffect(() => {
    if (serviceState.selectedVehicleId) {
      localStorage.setItem(SELECTED_VEHICLE_KEY, String(serviceState.selectedVehicleId));
    } else {
      localStorage.removeItem(SELECTED_VEHICLE_KEY);
    }
  }, [serviceState.selectedVehicleId]);

  useEffect(() => {
    try {
      if (serviceState.selectedVehicle) {
        localStorage.setItem(STORED_VEHICLE_KEY, JSON.stringify(serviceState.selectedVehicle));
      } else {
        localStorage.removeItem(STORED_VEHICLE_KEY);
      }
    } catch {
      // storage quota / private mode — non-critical
    }
  }, [serviceState.selectedVehicle]);

  const setSelectedVehicle = useCallback((vehicleId, vehicleData = {}) => {
    setServiceState((prev) => ({
      ...prev,
      selectedVehicleId: vehicleId,
      // Keep the full vehicle object so ServiceTwo/ServiceThree can render
      // the customer's vehicle without falling back to CarContext (which only
      // contains the logged-in worker's own vehicles).
      selectedVehicle: vehicleData.vehicle || null,
      currentMileage: vehicleData.currentMileage || 0,
      lastServiceMileage: vehicleData.lastServiceMileage || null,
      nextSuggestedMileage: vehicleData.nextSuggestedMileage || null,
    }));
  }, []);

  // Step 1: customer chosen via mobile search (existing) or manual form (new).
  const setSelectedCustomer = useCallback((customer) => {
    setServiceState((prev) => ({
      ...prev,
      selectedCustomer: customer
        ? {
            id: customer.id ?? null,
            firstName: customer.firstName || '',
            lastName: customer.lastName || '',
            phone: customer.phone || '',
            isNew: Boolean(customer.isNew),
          }
        : null,
    }));
  }, []);

  const updateMileage = useCallback((mileage) => {
    setServiceState((prev) => ({ ...prev, currentMileage: mileage }));
  }, []);

  // Next Service Mileage for the upcoming service (frontend state, persisted
  // per registration via details JSON + localStorage mirror; reserved for
  // future backend column without changing this interface).
  const setNextServiceMileage = useCallback((mileage) => {
    const n = mileage === null || mileage === undefined || mileage === "" ? null : Number(mileage);
    setServiceState((prev) => ({
      ...prev,
      nextServiceMileage: n === null || Number.isFinite(n) ? n : prev.nextServiceMileage,
    }));
  }, []);

  const toggleCategoryOption = useCallback((categoryId, option) => {
    setServiceState((prev) => ({
      ...prev,
      selectedCategories: {
        ...prev.selectedCategories,
        [categoryId]: prev.selectedCategories[categoryId] === option ? null : option,
      },
    }));
  }, []);

  const setCategoryDetail = useCallback((categoryId, detail) => {
    setServiceState((prev) => ({
      ...prev,
      categoryDetails: {
        ...prev.categoryDetails,
        [categoryId]: { ...prev.categoryDetails[categoryId], ...detail },
      },
    }));
  }, []);

  const setPendingServices = useCallback((services) => {
    setServiceState((prev) => ({ ...prev, pendingServices: Array.isArray(services) ? services : [] }));
  }, []);

  // Start each service-registration flow with a clean selection UI while
  // keeping the selected vehicle/customer and the pending snapshot intact.
  const clearServiceSelections = useCallback(() => {
    setServiceState((prev) => ({
      ...prev,
      selectedCategories: {},
      categoryDetails: {},
    }));
  }, []);

  const setServiceDate = useCallback((date) => {
    setServiceState((prev) => ({ ...prev, serviceDate: date }));
  }, []);

  const setCost = useCallback((cost) => {
    setServiceState((prev) => ({ ...prev, cost }));
  }, []);

  const setDescription = useCallback((description) => {
    setServiceState((prev) => ({ ...prev, description }));
  }, []);

  const setDetails = useCallback((details) => {
    setServiceState((prev) => ({ ...prev, details }));
  }, []);

  const resetService = useCallback(() => {
    try {
      localStorage.removeItem(SELECTED_VEHICLE_KEY);
      localStorage.removeItem(STORED_VEHICLE_KEY);
    } catch {}
    setServiceState(getInitialServiceState());
  }, []);

  useEffect(() => {
    window.addEventListener('tasmeh:account-logout', resetService);
    return () => window.removeEventListener('tasmeh:account-logout', resetService);
  }, [resetService]);

  const getSelectedCategories = useCallback(() => {
    return Object.entries(serviceState.selectedCategories)
      .filter(([, option]) => option !== null)
      .map(([categoryId, option]) => ({
        categoryId: Number(categoryId),
        action: option, // 'L' = تعویض, 'R' = بازدید
      }));
  }, [serviceState.selectedCategories]);

  const getSubmitData = useCallback(() => {
    const selectedCats = Object.entries(serviceState.selectedCategories)
      .filter(([, option]) => option !== null)
      .map(([categoryId, option]) => {
        const detail = serviceState.categoryDetails[categoryId] || {};
        return {
          categoryId: Number(categoryId),
          type: option === 'L' ? 'REPAIR' : 'PERIODIC_KM_TIME', // L=تعویض=REPAIR, R=بازدید=PERIODIC_KM_TIME
          action: option,
          cost: detail.cost || null,
          description: detail.description || '',
          details: detail.details || {},
        };
      });

    return {
      vehicleId: serviceState.selectedVehicleId,
      mileage: serviceState.currentMileage,
      nextServiceMileage: serviceState.nextServiceMileage,
      serviceDate: serviceState.serviceDate,
      categories: selectedCats,
    };
  }, [serviceState]);

  const value = {
    ...serviceState,
    setSelectedVehicle,
    setSelectedCustomer,
    updateMileage,
    setNextServiceMileage,
    toggleCategoryOption,
    setCategoryDetail,
    setPendingServices,
    clearServiceSelections,
    setServiceDate,
    setCost,
    setDescription,
    setDetails,
    resetService,
    getSelectedCategories,
    getSubmitData,
  };

  return (
    <ServiceContext.Provider value={value}>
      {children}
    </ServiceContext.Provider>
  );
}

export function useService() {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error("useService must be used within a ServiceProvider");
  }
  return context;
}

export default ServiceContext;
